import { useId, useState } from "react";
import type { FormEvent } from "react";
import {
  api,
  ApiError,
  ROLES,
  toApiError,
  unwrap,
  type AdminOrgOut,
  type OrgKind,
  type Role,
} from "../../api/client";
import { ErrorMessage, LiveMessage, Loading } from "../../components/ui";
import { t, type MessageKey } from "../../i18n/t";
import { formatDate, roleLabel } from "../../lib/format";
import { useQuery } from "../../lib/useQuery";

const KINDS: { value: OrgKind; label: MessageKey }[] = [
  { value: "kommune", label: "admin.kindKommune" },
  { value: "radgiver", label: "admin.kindRadgiver" },
  { value: "annet", label: "admin.kindAnnet" },
];

function kindLabel(kind: string): string {
  const k = KINDS.find((x) => x.value === kind);
  return k ? t(k.label) : kind;
}

const NUMMER_RE = /^[0-9]{4}$/;

/** Shared submit helper: run `action`, show its error inline, report success upwards. */
function useAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(toApiError(err));
    } finally {
      setBusy(false);
    }
  }
  return { busy, error, run };
}

function CreateOrgForm({ onCreated }: { onCreated: (org: AdminOrgOut) => void }) {
  const id = useId();
  const [name, setName] = useState("");
  const [kind, setKind] = useState<OrgKind>("kommune");
  const { busy, error, run } = useAction();

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    void run(async () => {
      const org = await unwrap(api.POST("/api/admin/orgs", { body: { name: name.trim(), kind } }));
      setName("");
      onCreated(org);
    });
  }

  return (
    <form className="card" onSubmit={onSubmit}>
      <h2>{t("admin.orgCreateTitle")}</h2>
      <div className="form-row">
        <div className="field">
          <label htmlFor={`${id}-name`}>{t("admin.orgName")}</label>
          <input
            id={`${id}-name`}
            required
            minLength={2}
            maxLength={200}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor={`${id}-kind`}>{t("admin.orgKind")}</label>
          <select
            id={`${id}-kind`}
            value={kind}
            onChange={(e) => setKind(e.target.value as OrgKind)}
          >
            {KINDS.map((k) => (
              <option key={k.value} value={k.value}>
                {t(k.label)}
              </option>
            ))}
          </select>
        </div>
        <div className="field field-action">
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {t("admin.orgCreate")}
          </button>
        </div>
      </div>
      {error && <ErrorMessage error={error} />}
    </form>
  );
}

function OrgCard({
  org,
  onUpdated,
  onMessage,
}: {
  org: AdminOrgOut;
  onUpdated: (org: AdminOrgOut) => void;
  onMessage: (msg: string) => void;
}) {
  const id = useId();
  const [nummer, setNummer] = useState("");
  const [navn, setNavn] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("owner");
  const assign = useAction();
  const invite = useAction();
  const unassign = useAction();
  const [nummerError, setNummerError] = useState<string | null>(null);

  function onAssign(e: FormEvent) {
    e.preventDefault();
    const nr = nummer.trim();
    if (!NUMMER_RE.test(nr)) {
      setNummerError(t("admin.nummerInvalid"));
      return;
    }
    setNummerError(null);
    void assign.run(async () => {
      const updated = await unwrap(
        api.POST("/api/admin/orgs/{org_id}/kommuner", {
          params: { path: { org_id: org.id } },
          body: { nummer: nr, navn: navn.trim() },
        }),
      );
      setNummer("");
      setNavn("");
      onUpdated(updated);
      onMessage(t("admin.assigned", { nummer: nr, org: org.name }));
    });
  }

  function onUnassign(nr: string, label: string) {
    void unassign.run(async () => {
      const updated = await unwrap(
        api.DELETE("/api/admin/orgs/{org_id}/kommuner/{nummer}", {
          params: { path: { org_id: org.id, nummer: nr } },
        }),
      );
      onUpdated(updated);
      onMessage(t("admin.unassigned", { kommune: label, org: org.name }));
    });
  }

  function onInvite(e: FormEvent) {
    e.preventDefault();
    void invite.run(async () => {
      const res = await unwrap(
        api.POST("/api/admin/orgs/{org_id}/invitations", {
          params: { path: { org_id: org.id } },
          body: { email: email.trim(), role },
        }),
      );
      setEmail("");
      onMessage(res.detail ?? t("members.inviteSent", { email }));
    });
  }

  return (
    <article className="card org-card" aria-labelledby={`${id}-title`}>
      <header className="org-card-header">
        <h3 id={`${id}-title`}>{org.name}</h3>
        <p className="muted small">
          {kindLabel(org.kind)} · {t("admin.orgMembers", { count: org.members })} ·{" "}
          {t("admin.orgCreatedAt", { date: formatDate(org.created_at) })}
        </p>
      </header>
      <h4>{t("admin.orgKommuner")}</h4>
      {org.kommuner.length === 0 ? (
        <p className="muted">{t("admin.orgNoKommuner")}</p>
      ) : (
        <ul className="chip-list">
          {org.kommuner.map((k) => {
            const label = `${k.nummer} ${k.navn}`.trim();
            return (
              <li key={k.nummer} className="chip">
                {label}
                <button
                  type="button"
                  className="chip-remove"
                  onClick={() => onUnassign(k.nummer, label)}
                  disabled={unassign.busy}
                  aria-label={t("admin.unassignLabel", { kommune: label, org: org.name })}
                >
                  ×
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {unassign.error && <ErrorMessage error={unassign.error} />}

      <div className="org-forms">
        <form onSubmit={onAssign} noValidate>
          <h4>{t("admin.assignTitle")}</h4>
          <div className="form-row">
            <div className="field">
              <label htmlFor={`${id}-nummer`}>{t("admin.kommuneNummer")}</label>
              <input
                id={`${id}-nummer`}
                inputMode="numeric"
                maxLength={4}
                required
                value={nummer}
                aria-describedby={`${id}-nummer-hint`}
                aria-invalid={nummerError ? true : undefined}
                onChange={(e) => setNummer(e.target.value)}
                className="input-short"
              />
              <span id={`${id}-nummer-hint`} className="hint">
                {nummerError ?? t("admin.kommuneNummerHint")}
              </span>
            </div>
            <div className="field">
              <label htmlFor={`${id}-navn`}>{t("admin.kommuneNavn")}</label>
              <input
                id={`${id}-navn`}
                maxLength={100}
                value={navn}
                onChange={(e) => setNavn(e.target.value)}
              />
            </div>
            <div className="field field-action">
              <button type="submit" className="btn btn-secondary" disabled={assign.busy}>
                {t("admin.assign")}
              </button>
            </div>
          </div>
          {assign.error && <ErrorMessage error={assign.error} />}
        </form>

        <form onSubmit={onInvite}>
          <h4>{t("admin.inviteTitle")}</h4>
          <div className="form-row">
            <div className="field">
              <label htmlFor={`${id}-email`}>{t("admin.inviteEmail")}</label>
              <input
                id={`${id}-email`}
                type="email"
                required
                autoComplete="off"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor={`${id}-role`}>{t("admin.inviteRole")}</label>
              <select
                id={`${id}-role`}
                value={role}
                onChange={(e) => setRole(e.target.value as Role)}
              >
                {[...ROLES].reverse().map((r) => (
                  <option key={r} value={r}>
                    {roleLabel(r)}
                  </option>
                ))}
              </select>
            </div>
            <div className="field field-action">
              <button type="submit" className="btn btn-secondary" disabled={invite.busy}>
                {t("admin.inviteSubmit")}
              </button>
            </div>
          </div>
          {invite.error && <ErrorMessage error={invite.error} />}
        </form>
      </div>
    </article>
  );
}

export function OrgsTab() {
  const orgs = useQuery("admin:orgs", (signal) => unwrap(api.GET("/api/admin/orgs", { signal })));
  const [message, setMessage] = useState("");

  function upsert(org: AdminOrgOut) {
    orgs.setData((prev) => {
      const list = prev ?? [];
      return list.some((o) => o.id === org.id)
        ? list.map((o) => (o.id === org.id ? org : o))
        : [...list, org].sort((a, b) => a.name.localeCompare(b.name, "nb"));
    });
  }

  return (
    <div>
      <LiveMessage message={message} />
      <CreateOrgForm
        onCreated={(org) => {
          upsert(org);
          setMessage(t("admin.orgCreated", { name: org.name }));
        }}
      />
      {orgs.error && <ErrorMessage error={orgs.error} onRetry={orgs.reload} />}
      {!orgs.data && !orgs.error && <Loading />}
      {orgs.data && orgs.data.length === 0 && <p>{t("admin.orgsEmpty")}</p>}
      {orgs.data?.map((org) => (
        <OrgCard key={org.id} org={org} onUpdated={upsert} onMessage={setMessage} />
      ))}
    </div>
  );
}
